import { Link } from 'react-router-dom'
import { LegalPageLayout, LegalSection, LegalParagraph, LegalList } from '@roselabs-io/saas-shell'
import { Header, Footer } from '../components'

export default function TermsPage() {
  return (
    <LegalPageLayout
      title="Terms of Service"
      lastUpdated="November 19, 2025"
      header={<Header />}
      footer={<Footer />}
      className="bg-slate-300"
    >
      <LegalSection number="1" title="Acceptance of Terms">
        <LegalParagraph>
          By accessing or using Falcon ("Service"), you agree to be bound by these Terms of Service.
          If you do not agree to these terms, please do not use the Service.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="2" title="Description of Service">
        <LegalParagraph>
          Falcon is a dependency vulnerability scanning service that analyzes your project lockfiles
          and reports known security vulnerabilities. The Service includes:
        </LegalParagraph>
        <LegalList>
          <li>Web-based vulnerability scanning</li>
          <li>API access for CI/CD integration</li>
          <li>Email alerts for detected vulnerabilities</li>
          <li>Scan history and analytics</li>
        </LegalList>
      </LegalSection>

      <LegalSection number="3" title="Account Registration">
        <LegalParagraph>
          To use certain features of the Service, you must register for an account. You agree to:
        </LegalParagraph>
        <LegalList>
          <li>Provide accurate and complete registration information</li>
          <li>Maintain the security of your account credentials</li>
          <li>Notify us immediately of any unauthorized access</li>
          <li>Accept responsibility for all activities under your account</li>
        </LegalList>
      </LegalSection>

      <LegalSection number="4" title="Acceptable Use">
        <LegalParagraph>You agree not to:</LegalParagraph>
        <LegalList>
          <li>Use the Service for any illegal purpose</li>
          <li>Attempt to gain unauthorized access to the Service</li>
          <li>Interfere with or disrupt the Service</li>
          <li>Upload malicious code or content</li>
          <li>Resell or redistribute the Service without permission</li>
          <li>Exceed your plan's usage limits through automated means</li>
        </LegalList>
      </LegalSection>

      <LegalSection number="5" title="Subscription and Billing">
        <LegalParagraph>
          Paid subscriptions are billed monthly. By subscribing, you authorize us to charge your
          payment method on a recurring basis. You may cancel at any time; cancellation takes effect
          at the end of your current billing period.
        </LegalParagraph>
        <LegalParagraph>
          We reserve the right to change pricing with 30 days notice. Continued use after price
          changes constitutes acceptance.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="6" title="Data and Privacy">
        <LegalParagraph>
          Your use of the Service is also governed by our{' '}
          <Link to="/privacy" className="text-accent-600 hover:text-accent-700">
            Privacy Policy
          </Link>
          . We process lockfiles to provide vulnerability scanning but do not access or store your
          source code.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="7" title="API Usage">
        <LegalParagraph>
          API keys are for your use only and must not be shared. You are responsible for all API
          calls made with your key. We may rate-limit or suspend access for excessive or abusive
          usage.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="8" title="Service Availability">
        <LegalParagraph>
          We strive for high availability but do not guarantee uninterrupted service. We may perform
          maintenance that temporarily affects availability. We are not liable for any downtime or
          service interruptions.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="9" title="Disclaimers">
        <LegalParagraph>
          The Service is provided "as is" without warranties of any kind. We do not guarantee that
          all vulnerabilities will be detected. The vulnerability data comes from third-party
          sources (OSV.dev) and may not be complete or accurate.
        </LegalParagraph>
        <LegalParagraph>
          You are responsible for evaluating and addressing any vulnerabilities reported by the
          Service.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="10" title="Limitation of Liability">
        <LegalParagraph>
          To the maximum extent permitted by law, we shall not be liable for any indirect,
          incidental, special, consequential, or punitive damages, including loss of profits, data,
          or business opportunities.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="11" title="Termination">
        <LegalParagraph>
          We may terminate or suspend your account for violations of these terms. Upon termination,
          your right to use the Service ceases immediately. Your data will be retained for 30 days
          after termination.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="12" title="Changes to Terms">
        <LegalParagraph>
          We may modify these terms at any time. We will notify you of material changes via email or
          through the Service. Continued use after changes constitutes acceptance.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="13" title="Contact Us">
        <LegalParagraph>
          If you have questions about these terms,{' '}
          <Link to="/contact" className="text-accent-600 hover:text-accent-700 transition-colors">
            contact us
          </Link>{' '}
          through our contact form with subject "Legal".
        </LegalParagraph>
      </LegalSection>
    </LegalPageLayout>
  )
}
